// Full SLA detail card on the ticket-show page. Shows all three clocks
// (first response / idle response / resolution) with target minutes,
// due_at (relative + absolute), and met/breach timestamps. Mirrors the
// payload emitted by Portal::TicketPresenter#sla_payload.

import { Card } from "@/components/ui/card"
import { fmtRelativeTime } from "@/lib/format"
import { cn } from "@/lib/cn"
import { SlaPill } from "@/components/domain/tickets/sla-pill"
import type {
  SlaState,
  SlaFirstResponseClock,
  SlaIdleResponseClock,
  SlaResolutionClock,
  TicketSla,
} from "@/types/ticket"

const STATE_LABEL: Record<SlaState, string> = {
  not_tracked: "Not tracked",
  not_yet:     "On track",
  near_breach: "Near breach",
  breached:    "Breached",
  met:         "Met",
}

type Props = { sla: TicketSla }

export function TicketSlaPanel({ sla }: Props) {
  const allUntracked =
    sla.firstResponse.state === "not_tracked" &&
    sla.idleResponse.state === "not_tracked" &&
    sla.resolution.state === "not_tracked"

  return (
    <Card variant="inset" className="space-y-4 p-4">
      <header className="flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-app-text">SLA</h2>
        {sla.nextDueAt && (
          <span className="text-[11px] text-app-muted">
            Next due {fmtRelativeTime(sla.nextDueAt).replace(" ago", " ago/from now")}
          </span>
        )}
      </header>

      {allUntracked && (
        <p className="text-xs text-app-subtle">
          No SLA configured for this ticket.
        </p>
      )}

      <FirstResponseRow clock={sla.firstResponse} />
      <IdleResponseRow  clock={sla.idleResponse} />
      <ResolutionRow    clock={sla.resolution} />
    </Card>
  )
}

function FirstResponseRow({ clock }: { clock: SlaFirstResponseClock }) {
  return (
    <Row
      title="First response"
      state={clock.state}
      targetMinutes={clock.targetMinutes}
      dueAt={clock.dueAt}
      pill={
        <SlaPill
          label="FR"
          state={clock.state}
          dueAt={clock.dueAt}
          metAt={clock.metAt}
          breachedAt={clock.breachedAt}
        />
      }
    >
      {clock.metAt && (
        <Detail label="Met at" iso={clock.metAt} />
      )}
      {clock.breachedAt && (
        <Detail label="Breached at" iso={clock.breachedAt} tone="error" />
      )}
    </Row>
  )
}

function IdleResponseRow({ clock }: { clock: SlaIdleResponseClock }) {
  return (
    <Row
      title="Idle response"
      state={clock.state}
      targetMinutes={clock.targetMinutes}
      dueAt={clock.dueAt}
      pill={
        <SlaPill
          label="Idle"
          state={clock.state}
          dueAt={clock.dueAt}
          breachedAt={clock.firstBreachedAt}
          breachCount={clock.breachCount}
        />
      }
    >
      {clock.breachCount > 0 && (
        <div className="text-[11px] text-app-muted">
          Breached <span className="font-semibold">{clock.breachCount}</span>{" "}
          time{clock.breachCount === 1 ? "" : "s"} on this ticket
        </div>
      )}
      {clock.firstBreachedAt && (
        <Detail label="First breached" iso={clock.firstBreachedAt} tone="error" />
      )}
      {clock.lastBreachedDueAt && (
        <Detail label="Last missed due" iso={clock.lastBreachedDueAt} tone="error" />
      )}
    </Row>
  )
}

function ResolutionRow({ clock }: { clock: SlaResolutionClock }) {
  return (
    <Row
      title="Resolution"
      state={clock.state}
      targetMinutes={clock.targetMinutes}
      dueAt={clock.dueAt}
      pill={
        <SlaPill
          label="Res"
          state={clock.state}
          dueAt={clock.dueAt}
          breachedAt={clock.breachedAt}
        />
      }
    >
      {clock.breachedAt && (
        <Detail label="Breached at" iso={clock.breachedAt} tone="error" />
      )}
    </Row>
  )
}

function Row({
  title,
  state,
  targetMinutes,
  dueAt,
  pill,
  children,
}: {
  title: string
  state: SlaState
  targetMinutes: number | null
  dueAt: string | null
  pill: React.ReactNode
  children?: React.ReactNode
}) {
  const dim = state === "not_tracked"
  return (
    <div className={cn("space-y-1", dim && "opacity-60")}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-medium text-app-text">{title}</div>
        <div className="text-[11px] uppercase tracking-wide text-app-subtle">
          {STATE_LABEL[state]}
        </div>
      </div>
      <div>{pill}</div>
      <div className="ml-9 space-y-0.5">
        {targetMinutes != null && (
          <div className="text-[11px] text-app-muted">
            Target: {fmtTarget(targetMinutes)}
          </div>
        )}
        {dueAt && state !== "met" && (
          <Detail label="Due" iso={dueAt} />
        )}
        {state === "not_tracked" && (
          <div className="text-[11px] text-app-subtle">
            No SLA configured for this ticket.
          </div>
        )}
        {children}
      </div>
    </div>
  )
}

function Detail({
  label, iso, tone,
}: { label: string; iso: string; tone?: "error" }) {
  const d = new Date(iso)
  const absolute = Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-US", {
        month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
      })
  return (
    <div className={cn("text-[11px]", tone === "error" ? "text-status-error" : "text-app-muted")}>
      {label}: {absolute} <span className="opacity-70">({fmtRelativeTime(iso)})</span>
    </div>
  )
}

function fmtTarget(minutes: number): string {
  if (minutes < 60) return `${minutes}m`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h < 24) return m ? `${h}h ${m}m` : `${h}h`
  const d = Math.floor(h / 24)
  const hr = h % 24
  return hr ? `${d}d ${hr}h` : `${d}d`
}
